<template lang="pug">
section.informacoes-gerais-v2(v-if='informacoesGerais || produtividade || protocoloUtilizadoFormatado || exibirCapacidade')
    h4.informacoes-gerais-v2__titulo {{ $t('mensagens.diagnosticoSocioambiental.informacoesGerais') }}
    el-divider.my-2
    .row(v-if='protocoloUtilizadoFormatado')
        .col-md-12.mb-2
            small.d-block.text-muted {{ $t('mensagens.diagnosticoSocioambiental.protocoloUtilizado') }}
            span.informacoes-gerais-v2__valor {{ protocoloUtilizadoFormatado }}
    .row(v-if='informacoesGerais')
        .col-md-4.mb-2(v-for='campo in camposVisiveis' :key='campo.label')
            small.d-block.text-muted {{ campo.label }}
            span.informacoes-gerais-v2__valor {{ campo.valor }}
        .col-md-4.mb-2(v-if='mostrarLinkSicar')
            p.text-right.text-info(@click='abrirSicar')
                span.link-button(style='cursor: pointer') {{ $t('mensagens.diagnosticoSocioambiental.verDemonstrativoSicar') }}
    el-divider.my-2(v-if='mostrarCapacidade')
    section.mt-3(v-if='mostrarCapacidade')
        .d-flex.flex-wrap.justify-content-between.align-items-center
            el-tooltip(:content="$t('mensagens.diagnosticoSocioambiental.tooltipCapacidadeProdutiva')" placement='top')
                h4.mb-0.informacoes-gerais-v2__titulo
                    i.mdi.mdi-information-outline.mr-1
                    | {{ $t('mensagens.diagnosticoSocioambiental.capacidadeProdutiva') }}
            el-button.text-nowrap(v-if='culturas.length' type='text' @click='modalProdutividade = true')
                | {{ $t('mensagens.diagnosticoSocioambiental.verPorCultura') }}
                i.el-icon-arrow-right.el-icon--right
        template(v-if='culturas.length')
            small.d-block.text-muted.mb-2(v-if='rotuloDaSafra') {{ rotuloDaSafra }}
            .row
                .col-md-4.mb-2(v-for='card in cardsCapacidade' :key='card.chave')
                    small.d-block.text-muted {{ card.rotulo }}
                    span.informacoes-gerais-v2__valor.text-nowrap {{ card.capacidade }}
                    span.text-muted.text-nowrap(v-if='card.sacas') {{ ' · ' + card.sacas }}
            ModalProdutividadeCulturas(:visible.sync='modalProdutividade' :produtividade='produtividade')
        //- RN34: consulta por CAR sem dado na tabela de inteligência mostra a seção com "--" em todos os campos
        template(v-else)
            IndicadoresCapacidadeProdutiva.mt-2(:indicadores='indicadoresSemDado')
            small.d-block.text-muted.mt-2 {{ $t('mensagens.diagnosticoSocioambiental.fonteCapacidadeProdutiva') }}
</template>

<script>
import {
    SEM_DADO,
    formatarCapacidade,
    formatarCapacidadeSacas,
    indicadoresCapacidade,
    nomeCultura,
    rotuloSafra,
    safrasDiferem
} from '@/utils/produtividadeCultura';
import formatters from '@/utils/formatters';
import IndicadoresCapacidadeProdutiva from './IndicadoresCapacidadeProdutiva.vue';
import ModalProdutividadeCulturas from './ModalProdutividadeCulturas.vue';

// Origens equivalentes a CAR (imóvel cadastrado) x SIGEF/arquivo vetorial (sem CAR).
// Mirror: spec.md FR-6 (brain/features/story-GS-3925) + CardInformacoesGeraisCar.vue (v-if
// tipoPesquisa === 'CODIGOCAR').
const TIPOS_CAR = ['CODIGOCAR', 'CAR_ESTADUAL_MT', 'CPF_CNPJ'];

export default {
    name: 'InformacoesGeraisV2',
    components: { IndicadoresCapacidadeProdutiva, ModalProdutividadeCulturas },
    props: {
        informacoesGerais: { type: Object, default: null },
        imovel: { type: Object, default: null },
        produtividade: { type: Object, default: null },
        protocoloUtilizadoFormatado: { type: String, default: null },
        tipo: { type: String, default: null },
        pesquisado: { type: String, default: null },
        // a consulta é por CAR (RN26): a seção aparece mesmo sem dado na tabela de inteligência (RN34)
        exibirCapacidade: { type: Boolean, default: false }
    },
    data() {
        return {
            modalProdutividade: false
        };
    },
    computed: {
        // `produtividade` é o objeto do Lyra (safra, origem, parcial, culturas[]); nulo quando o imóvel não tem
        // dado na tabela de inteligência (204).
        culturas() {
            return this.produtividade && Array.isArray(this.produtividade.culturas) ? this.produtividade.culturas : [];
        },
        mostrarCapacidade() {
            return this.exibirCapacidade || this.culturas.length > 0;
        },
        indicadoresSemDado() {
            return indicadoresCapacidade(null, this.locale);
        },
        locale() {
            return this.$i18n.locale;
        },
        // GS-4110: culturas em safras diferentes mostram a safra junto do nome de cada uma, não uma só acima dos cards
        safraPorCultura() {
            return safrasDiferem(this.culturas);
        },
        rotuloDaSafra() {
            return this.safraPorCultura ? null : rotuloSafra(this.produtividade && this.produtividade.safra, this.$t.bind(this));
        },
        // $t e $te precisam do `this` do componente (vue-i18n lê this.$i18n): passar `$t` solto a um util quebra o render
        cardsCapacidade() {
            const t = this.$t.bind(this);
            const te = this.$te.bind(this);
            return this.culturas.map(cultura => {
                const nome = nomeCultura(cultura.cultura, t, te);
                const safra = this.safraPorCultura ? rotuloSafra(cultura.safra, t) : null;
                return {
                    chave: cultura.cultura,
                    nome,
                    rotulo: safra ? `${nome} · ${safra}` : nome,
                    capacidade: formatarCapacidade(cultura, this.locale),
                    sacas: this.semSacasNoResumo(formatarCapacidadeSacas(cultura, this.locale))
                };
            });
        },
        origemCar() {
            return TIPOS_CAR.includes(this.tipo);
        },
        mostrarLinkSicar() {
            return this.tipo === 'CODIGOCAR';
        },
        // Mirror: CardInformacoesGeraisCar.vue:12,144-168,33,34-39,52-55,58-72 (campos que a v1
        // já exibe corretamente) + DadosSocioAmbientalDTO.java:36-81 (nomes reais dos campos do
        // backend). O InformacoesGeraisV2 anterior lia estadoMunicipio/biomaFormatado/
        // faixaFronteiraFormatado, que não existem no DTO - copiados por engano da tela do PS
        // (DiagnosticosSocioambientais.vue:300,313,315), que tem o mesmo bug.
        camposVisiveis() {
            const dados = this.informacoesGerais || {};
            const campos = [
                // RN25: o rótulo é sempre "Município/UF". A chave `municipioUf` vale "Localidade" e é usada por outras 9
                // telas (v1 inclusive), por isso a v2 tem chave própria (GS-4061).
                { label: this.$t('mensagens.diagnosticoSocioambiental.rotuloMunicipioUf'), valor: dados.localidade },
                { label: this.$t('mensagens.diagnosticoSocioambiental.propriedadesCamada.areaHa'), valor: this.areaHaExibicao(dados) },
                { label: this.$t('mensagens.diagnosticoSocioambiental.areaAgricultavel'), valor: dados.areaAgricultavelHaFormatada },
                { label: this.$t('mensagens.diagnosticoSocioambiental.propriedadesCamada.bioma'), valor: dados.bioma }
            ];

            if (!this.origemCar) {
                campos.push({
                    label: this.$t('mensagens.diagnosticoSocioambiental.faixaDeFronteira'),
                    valor: this.formatarBooleano(dados.faixa_fronteira)
                });
            }

            if (this.origemCar) {
                campos.push(
                    { label: this.$t('mensagens.diagnosticoSocioambiental.propriedadesCamada.situacao'), valor: this.validaSituacao(dados.situacao) },
                    { label: this.$t('mensagens.diagnosticoSocioambiental.condicao'), valor: dados.condicao }
                );
                if (this.tipo === 'CODIGOCAR') {
                    campos.push(
                        { label: this.$t('mensagens.diagnosticoSocioambiental.moduloFiscal'), valor: dados.moduloFiscal },
                        { label: `${this.$t('mensagens.diagnosticoSocioambiental.aderiu')} PRA`, valor: this.formatarBooleano(dados.aderiuPra) },
                        { label: this.$t('mensagens.diagnosticoSocioambiental.situacaoReservaLegal'), valor: dados.situacaoReservaLegal },
                        { label: this.$t('mensagens.diagnosticoSocioambiental.dataAtualizacaoDados'), valor: dados.camadaCar?.dataAtualizacaoDados }
                    );
                }
            }

            return campos.filter(campo => campo.valor !== null && campo.valor !== undefined && campo.valor !== '');
        }
    },
    methods: {
        areaHaExibicao(dados) {
            if (this.imovel && this.imovel.areaHa != null) {
                return formatters.formatNumber(this.imovel.areaHa, 2);
            }
            return dados.areaHaFormatada || dados.areaHa || dados.area_ha;
        },
        // No resumo as sacas só acompanham uma capacidade que existe; o "--" fica para o modal e para a seção sem dado.
        semSacasNoResumo(sacas) {
            return sacas === SEM_DADO ? null : sacas;
        },
        // Mirror: DiagnosticoSocioambiental.vue:462-475 (v1)
        validaSituacao(situacao) {
            switch (situacao) {
                case 'AT':
                    return this.$t('mensagens.diagnosticoSocioambiental.situacoes.ativo');
                case 'PE':
                    return this.$t('mensagens.diagnosticoSocioambiental.situacoes.pendente');
                case 'SU':
                    return this.$t('mensagens.diagnosticoSocioambiental.situacoes.suspenso');
                case 'CA':
                    return this.$t('mensagens.diagnosticoSocioambiental.situacoes.cancelado');
                case 'RE':
                    return this.$t('mensagens.diagnosticoSocioambiental.situacoes.retificado');
                default:
                    return situacao;
            }
        },
        formatarBooleano(valor) {
            if (valor === null || valor === undefined) return null;
            return valor ? this.$t('mensagens.comuns.sim') : this.$t('mensagens.comuns.nao');
        },
        // Mirror: CardInformacoesGeraisCar.vue:217-227
        abrirSicar() {
            navigator.clipboard.writeText(this.pesquisado);
            this.$notify.success({
                message: 'O número do CAR foi copiado para sua área de transferência, você está sendo direcionado para o site do SICAR'
            });
            setTimeout(() => {
                window.open('https://www.car.gov.br/#/consultar', '_blank');
            }, 3000);
        }
    }
};
</script>

<style lang="scss" scoped>
// Mirror: CardInformacoesGeraisCar.vue (PS) - título em negrito com a cor de texto padrão, e o
// VALOR (não o rótulo) em negrito. O rótulo continua pequeno/discreto (text-muted), como já era.
.informacoes-gerais-v2 {
    &__titulo {
        font-size: 14px;
        font-weight: 500;
        color: var(--primary-font-color);
    }

    &__valor {
        font-weight: 500;
    }
}
</style>
